import {
  type MouseEventHandler,
  type ReactElement,
  type ReactNode,
  type Ref,
  cloneElement,
} from 'react';
import { NavLink, type NavLinkProps } from 'react-router-dom';

import classNames from 'classnames';

import style from './NavItem.module.css';

import {
  type ButtonProps,
  type ButtonType,
  type ButtonVariant,
} from '../Button';
import { Text } from '../Text';
import { MenuTrigger } from './MenuTrigger';

interface BaseNavigationItem {
  className?: string;
  label: string;
  orientation?: 'vertical' | 'horizontal';
  activeStyle?: 'line' | 'highlight';
}

type AnchorNavigationItem = BaseNavigationItem &
  JSX.IntrinsicElements['a'] & {
    type: 'anchor';
    href: string;
  };

type ButtonNavigationItem = BaseNavigationItem & {
  type: 'button';
} & (
    | {
        button: ReactElement<ButtonProps<ButtonType, ButtonVariant>>;
      }
    | (Omit<JSX.IntrinsicElements['button'], 'onClick'> & {
        buttonRef?: Ref<HTMLButtonElement>;
        children?: ReactNode;
        onClick: MouseEventHandler;
      })
  );

// defaults to type='link'
interface LinkNavigationItem extends BaseNavigationItem {
  type?: 'link';
  isActive?: NavLinkProps['isActive'];
  linkRef?: Ref<HTMLAnchorElement>;
  to: NavLinkProps['to'];
  onClick?: NavLinkProps['onClick'];
}

interface MenuNavigationItem extends BaseNavigationItem {
  type: 'menu';
  menuItems: { label: string; to: string }[];
}

interface TextNavigationItem extends BaseNavigationItem {
  type: 'text';
}

/**
 * A navigation item can be either a link (internal or external, e.g. an
 * anchor), a button, text, or a menu.
 */
export type NavigationItem =
  | AnchorNavigationItem
  | ButtonNavigationItem
  | MenuNavigationItem
  | TextNavigationItem
  | LinkNavigationItem;

export const isLinkItem = (
  navProps: NavigationItem
): navProps is LinkNavigationItem => 'to' in navProps;

/**
 * Render a {@link NavigationItem} as a link (internal or external, e.g. anchor),
 * button, menu, or text label. This component is meant to create links, buttons
 * or labels in containers with a navigation role - such as {@link AppNav}.
 *
 * @cssVariable --tfd-nav-item-color The color of each navigation item's text
 * @cssVariable --tfd-nav-item-active-color The color of the text when active or hovered
 * @cssVariable --tfd-nav-item-spacing The spacing between each item when the orientation is horizontal
 * @cssVariable --tfd-nav-item-font The font of each item when the orientation is horizontal
 * @cssVariable --tfd-nav-item-letter-spacing The letter spacing of each item when the orientation is horizontal
 * @cssVariable --tfd-nav-item-vertical-font The font when the orientation is vertical
 * @cssVariable --tfd-nav-item-vertical-letter-spacing The letter spacing when the orientation is vertical
 * @see https://corgi-x.tfd.engineering/components/navitem
 */
export function NavItem(props: NavigationItem) {
  const { label, orientation, type = 'link', ...restProps } = props;

  const className = classNames(
    style[type ?? 'link'],
    style.navItem,
    style[orientation ?? 'horizontal'],
    props.className
  );

  if (type === 'text') {
    return <Text className={className}>{label}</Text>;
  }

  if (type === 'menu' && 'menuItems' in restProps) {
    return (
      <MenuTrigger
        menuItems={restProps.menuItems}
        label={label}
        className={classNames(
          style[restProps.activeStyle ?? 'highlight'],
          className
        )}
      />
    );
  }

  if (type === 'anchor' && 'href' in restProps) {
    return (
      <a
        {...restProps}
        className={classNames(
          style[restProps.activeStyle ?? 'highlight'],
          className
        )}
      >
        {label}
      </a>
    );
  }

  if (type === 'button') {
    const buttonNavigationProps = restProps as ButtonNavigationItem;
    if ('button' in buttonNavigationProps) {
      const buttonProps = {
        type: 'link',
        ...buttonNavigationProps.button.props,
      } as ButtonProps;

      return cloneElement(
        buttonNavigationProps.button,
        {
          ...buttonProps,
          // specifically excludes the `button` style
          className: classNames(
            style.navItem,
            style[orientation ?? 'horizontal'],
            props.className
          ),
        },
        label
      );
    } else {
      const { activeStyle, buttonRef, children, ...buttonProps } =
        buttonNavigationProps;
      return (
        <button
          {...buttonProps}
          className={classNames(style[activeStyle ?? 'highlight'], className)}
          ref={buttonRef}
        >
          {children ?? label}
        </button>
      );
    }
  }

  const linkProps = restProps as LinkNavigationItem;
  return (
    <NavLink
      to={linkProps.to}
      className={active =>
        classNames(
          active && style.selected,
          style[linkProps.activeStyle ?? 'highlight'],
          className
        )
      }
      isActive={linkProps.isActive}
      ref={linkProps.linkRef}
      onClick={linkProps.onClick}
    >
      {label}
    </NavLink>
  );
}
